import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FileDown } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ApiError } from "@/services/BaseService";
import { useStartGuestExport } from "@/hooks/useDataExport";
import { ExportJobStatusView } from "./ExportJobStatusView";

interface GuestExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Manual SUPER_ADMIN entry point for guest export. Guests have no platform-level
 * screen, so the audit page lets an admin start an export by guest ID + tenant ID,
 * then polls the returned job.
 */
export const GuestExportDialog: React.FC<GuestExportDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const startGuest = useStartGuestExport();

  const [phase, setPhase] = useState<"form" | "status">("form");
  const [guestId, setGuestId] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);

  const close = () => {
    setPhase("form");
    setGuestId("");
    setTenantId("");
    setServerError(null);
    setJobId(null);
    onOpenChange(false);
  };

  const canSubmit = Boolean(guestId.trim() && tenantId.trim());

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit || startGuest.isPending) {
      return;
    }
    try {
      const job = await startGuest.mutateAsync({
        id: guestId.trim(),
        dto: { tenantId: tenantId.trim() },
      });
      toast.success(t("dataExport.job.toast.started"));
      setJobId(job.id);
      setServerError(null);
      setPhase("status");
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 409) {
        toast.error(
          t("dataExport.job.toast.inFlight", {
            target: t("dataExport.targetWord.GUEST"),
          }),
        );
        close();
        return;
      }
      if (error instanceof ApiError && error.statusCode === 404) {
        toast.error(t("dataExport.job.toast.notFound"));
        close();
        return;
      }
      setServerError(error instanceof Error ? error.message : String(error));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          close();
        }
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        {phase === "form" ? (
          <form
            onSubmit={(event) => void handleSubmit(event)}
            className="flex flex-col gap-5"
          >
            <DialogHeader>
              <div className="flex items-center gap-2 text-foreground">
                <FileDown className="size-5 text-royal-glow-3" />
                <DialogTitle>{t("dataExport.guestStart.title")}</DialogTitle>
              </div>
            </DialogHeader>

            <p className="text-sm text-foreground">
              {t("dataExport.guestStart.lead")}
            </p>

            <Input
              label={t("dataExport.guestStart.guestId")}
              value={guestId}
              onChange={(event) => setGuestId(event.target.value)}
              autoComplete="off"
              autoFocus
            />
            <div className="flex flex-col gap-1.5">
              <Input
                label={t("dataExport.guestStart.tenantId")}
                value={tenantId}
                onChange={(event) => setTenantId(event.target.value)}
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                {t("dataExport.guestStart.tenantIdHelp")}
              </p>
            </div>

            {serverError ? (
              <div className="rounded-md border border-custom-destructive-dark/40 bg-custom-destructive-dark/5 p-3 text-sm text-custom-destructive-dark">
                {serverError}
              </div>
            ) : null}

            <DialogFooter>
              <Button
                type="button"
                variant="new_outline"
                onClick={close}
                disabled={startGuest.isPending}
              >
                {t("dataExport.confirm.cancel")}
              </Button>
              <Button
                type="submit"
                variant="new_default"
                disabled={!canSubmit || startGuest.isPending}
              >
                {startGuest.isPending
                  ? t("dataExport.guestStart.submitting")
                  : t("dataExport.guestStart.submit")}
              </Button>
            </DialogFooter>
          </form>
        ) : null}

        {phase === "status" && jobId ? (
          <ExportJobStatusView key={jobId} jobId={jobId} onClose={close} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
